import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { APIuuid, IAPIArrayData } from '@shared/interfaces/api.interface';
import { IDocument } from '@shared/interfaces/document.interface';
import { IFilter } from '@shared/interfaces/filter.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { IReference } from '@shared/interfaces/reference.interface';
import { Document } from '@shared/models/document.model';
import { EMPTY, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CandidatesDocumentsService implements IPaginable {
  private documentsSource = new Subject();
  public documentsCalled$ = this.documentsSource.asObservable();

  private readonly ENDPOINT_V3: string = String(
    `${environment.ENDPOINTS.BACK_END.V3}/candidates`
  );

  constructor(private readonly _http: HttpClient) {}

  public refresh(v?: IReference) {
    this.documentsSource.next(v);
  }

  public getDownloadUrl(
    candidateId: APIuuid,
    documentId: APIuuid
  ): Observable<string> {
    return this._http
      .get<{ data: { url: string } }>(
        `${this.ENDPOINT_V3}/${candidateId}/documents/${documentId}/download`
      )
      .pipe(map((res: { data: { url: string } }) => res.data.url));
  }

  /**
   * Opens a candidate document in a new tab.
   *
   * The storage URL is short-lived and only minted by the API, which records
   * the download in the candidate's activity feed. The tab is opened
   * synchronously on the click and pointed at the URL once it arrives: a
   * `window.open` after the round trip is blocked as a popup.
   */
  public openDocument(
    candidateId: APIuuid,
    documentId: APIuuid
  ): Observable<string> {
    const tab: Window | null = window.open('', '_blank');

    if (tab) {
      tab.opener = null;
    }

    return this.getDownloadUrl(candidateId, documentId).pipe(
      tap({
        next: (url: string) => {
          if (tab) {
            tab.location.href = url;
          } else {
            window.location.assign(url);
          }
        },
        error: () => tab?.close()
      })
    );
  }

  public getPage(
    page: number,
    onGetRawData?: (data: IAPIArrayData) => void,
    filter?: IFilter
  ): Observable<IDocument[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('page[number]', String(page));

    const id = filter?.value;

    if (!Boolean(id)) {
      return EMPTY;
    }

    return this._http
      .get<IAPIArrayData>(`${this.ENDPOINT_V3}/${id}/documents`, {
        params
      })
      .pipe(
        tap((data: IAPIArrayData) => onGetRawData && onGetRawData(data)),
        map((res: IAPIArrayData) => Document.fromAPIArray(res))
      );
  }
}
